import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * <ambera-notify> — the back-in-stock dialog (snippets/ambera-notify-dialog.liquid).
 *
 * Opens from any `[data-notify-handle]` trigger on the page (the sold-out card's "Anunță-mă").
 * The trigger is a link to the product, so the listener runs in the capture phase and cancels
 * the navigation before the product card sees the click.
 *
 * The form is Shopify's newsletter (`customer`) form. It is posted in the background: Shopify
 * redirects to `?customer_posted=true` when the email is taken, re-renders the page with errors
 * when it is not, and sends bots to `/challenge`. On a challenge the form is submitted the normal
 * way, and the dialog reopens on the thank-you view when the shopper lands back here.
 */
class AmberaNotify extends HTMLElement {
  /** @type {HTMLDialogElement} */
  #dialog;
  /** @type {HTMLFormElement} */
  #form;
  /** @type {Element | null} Where focus goes back to when the dialog closes. */
  #returnFocus = null;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    const form = this.querySelector('form');
    if (!dialog || !form) return;
    this.#dialog = dialog;
    this.#form = form;

    document.addEventListener('click', this.#onTrigger, { capture: true });
    dialog.addEventListener('click', this.#onDialogClick);
    dialog.addEventListener('close', this.#onClose);
    form.addEventListener('submit', this.#onSubmit);
    this.#input?.addEventListener('input', () => this.#showError(false));

    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true' && window.location.hash === `#${form.id}`) {
      this.#show('success');
      this.#open();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onTrigger, { capture: true });
  }

  get #input() {
    return /** @type {HTMLInputElement | null} */ (this.#form.querySelector('input[type="email"]'));
  }

  /** @param {MouseEvent} event */
  #onTrigger = (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-notify-handle]');
    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const { notifyHandle = '', notifyProduct = '' } = trigger.dataset;
    const tags = this.#form.querySelector('[data-notify-tags]');
    if (tags instanceof HTMLInputElement) {
      tags.value = ['newsletter', 'back-in-stock', notifyHandle && `stoc-${notifyHandle}`].filter(Boolean).join(',');
    }
    const name = this.querySelector('[data-notify-name]');
    if (name instanceof HTMLElement) {
      name.textContent = notifyProduct;
      name.hidden = !notifyProduct;
    }

    this.#returnFocus = trigger;
    this.#show('form');
    this.#open();
    this.#input?.focus();
  };

  /**
   * Closes on the close buttons and on a click on the backdrop (the dialog box itself).
   * @param {MouseEvent} event
   */
  #onDialogClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target === this.#dialog || event.target.closest('[data-notify-close]')) this.#dialog.close();
  };

  #onClose = () => {
    unlockScroll(this.#dialog);
    if (this.#returnFocus instanceof HTMLElement) this.#returnFocus.focus({ preventScroll: true });
    this.#returnFocus = null;
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const submit = this.#form.querySelector('[type="submit"]');
    if (submit?.getAttribute('aria-busy') === 'true') return;
    submit?.setAttribute('aria-busy', 'true');
    this.#showError(false);

    try {
      const response = await fetch(this.#form.action, {
        method: 'POST',
        body: new FormData(this.#form),
        headers: { Accept: 'text/html' },
      });
      const url = new URL(response.url);

      if (url.pathname.includes('/challenge')) {
        this.#form.submit();
        return;
      }
      if (response.ok && url.searchParams.get('customer_posted') === 'true') {
        this.#form.reset();
        this.#show('success');
        return;
      }
      this.#showError(true);
    } catch {
      this.#showError(true);
    } finally {
      submit?.removeAttribute('aria-busy');
    }
  };

  #open() {
    if (this.#dialog.open) return;
    this.#dialog.showModal();
    lockScroll(this.#dialog);
  }

  /** @param {'form' | 'success'} view */
  #show(view) {
    for (const panel of this.querySelectorAll('[data-notify-view]')) {
      if (panel instanceof HTMLElement) panel.hidden = panel.dataset.notifyView !== view;
    }
    this.#showError(false);
    if (view === 'success') {
      const title = this.querySelector('[data-notify-success-title]');
      if (title instanceof HTMLElement) title.focus();
    }
  }

  /** @param {boolean} visible */
  #showError(visible) {
    const error = this.querySelector('[data-notify-error]');
    if (error instanceof HTMLElement) error.hidden = !visible;
    if (visible) this.#input?.setAttribute('aria-invalid', 'true');
    else this.#input?.removeAttribute('aria-invalid');
  }
}

if (!customElements.get('ambera-notify')) customElements.define('ambera-notify', AmberaNotify);
